// from https://developers.klaviyo.com/en/docs/track_klaviyo_form_activity_using_javascript

import {readCachedLocalStorage, STORAGE_CACHE_KEYS, writeCachedLocalStorage} from "../Session/cachedLocalStorage";
import {getTaggedLogger} from "../Session/taggedLogging";
import {setPopupClosed, setPopupOpen} from "../lib/popupStatus";
import {stashUser} from "../lib/userStash";
import {pushDataLayerEvent} from "../Analytics/GoogleDatalayer";

const STORAGE_KEY = STORAGE_CACHE_KEYS.KLAVIYO_FORMS; // klaviyoFormHistory

const logger = getTaggedLogger('klaviyo');

/**
 * Called once by KlaviyoInformant at application startup, this will register an event
 * handler.
 *
 * @param callback
 */
export function trapKlaviyoFormEvents({callback} = {}) {

  window.addEventListener("klaviyoForms", function (e) {
    logger.log(`Klaviyo Form Event: ${JSON.stringify(e, null, 2)}`, e);

    // Automatically call our standard handler, which will update the cached
    // user in local storage.
    handleKlaviyoFormEvent(e);

    // ...and an optional callback.
    if (callback) {
      callback(e);
    }
  });
}

export function readKlaviyoFormHistory() {
  return readCachedLocalStorage(STORAGE_KEY, {defaultValue: {}});
}

export function writeKlaviyoFormHistory(formStatus) {
  writeCachedLocalStorage(STORAGE_KEY, formStatus);

  logger.info("updated klaviyo status: ", formStatus);
  return formStatus;
}

export function handleKlaviyoFormEvent(event) {
  event ||= {};

  // add to our Klaviyo history in localStorage
  let entry = updateKlaviyoFormHistoryFromEvent(event);

  // update cached user in local storage for future identify() calls
  updateCachedUserFromEvent(event);

  // and update popupsOpen = { klaviyo-ID => true } in Redux
  updatePopupStatusFromEvent(event);

  return entry;
}

function getUserFromEvent(event) {
  if (!event) return null;

  const {detail = {}} = event;

  const {metaData = {}} = detail;
  const {$email: email, $phone_number: phone} = (metaData || {});

  // got an email address? Save to localStorage right away,
  // so future klaviyo identify() calls will get it
  if (!email) return null;

  return {
    email, phone,
    source: 'klaviyo'
  }
}

// put signup event in data layer only once, at least until the page loads again.
let reportedToDataLayer = false;

/**
 * If user info is found in event, update in our local cache.
 * 2025-10 stashUser() returns a promise now because Epsilon wants
 * email_sha256 and that runs asynchronously.
 *
 * @param event
 */
function updateCachedUserFromEvent(event) {
  let user = getUserFromEvent(event);
  if (!user)
    return;

  // write to LocalStorage and Redux
  stashUser(user).then(user => {
    if (!reportedToDataLayer) {
      pushDataLayerEvent({
        event: 'klaviyo_signup',
        email_sha256: user.email_sha256
      });
      reportedToDataLayer = true;  // don't do it again on this page load.
    }
  })
}

function updatePopupStatusFromEvent(event) {
  if (!event) return null;

  const {detail = {}} = event;

  const {formId, type: eventType} = detail;

  if (!formId) {
    logger.error("no form ID in klaviyo event: ", event);
    return;
  }

  const popupName = `klaviyo-${formId}`;
  if (eventType === 'close') {
    setPopupClosed(popupName);
  } else {
    setPopupOpen(popupName);
  }

}

function updateKlaviyoFormHistoryFromEvent(event) {
  if (!event) return null;

  let now = new Date();

  const {detail = {}} = event;

  const {formId, type: eventType, metaData = {}} = detail;
  const {$email, $phone_number} = (metaData || {});

  if (!formId) {
    logger.error("no form ID in klaviyo event: ", event);
    return;
  }

  let formStatus = readKlaviyoFormHistory();

  let entry = (formStatus[formId] ||= {formId, openCount: 0, touchedAt: now.getTime()});

  // maintain a list of events that have happened
  entry.eventList ||= [];
  entry.eventList.push(eventType);

  if ($email) {
    entry.email = $email;
  }

  if ($phone_number) {
    entry.phoneNumber = $phone_number;
  }

  switch (eventType) {
    case "open":
      entry.status = 'open';
      entry.openCount = 1 + (+entry.openCount);
      entry.openedAt = now.getTime();
      //entry.eventList = [ 'open' ];  // and reset eventList
      break;
    case "submit":  // typically followed soon by 'close'
      entry.status = 'submitted';
      entry.submitted = true;
      break;
    case "close":
      entry.status = 'closed';
      entry.closedAt = now.getTime();
      break;
    default:
      logger.info(`ignoring klaviyo form event ${eventType} on form ${formId}`)
  }

  writeKlaviyoFormHistory(formStatus);
  return entry;
}

//

/*
 // klaviyo.openForm('SAraHJ')

 grab event.detail from each:
 good events:
 stepSubmit - multiple times per form
 submit - once, has main payload

 detail.metaData.{email, phone_number, stepName, ...additional fields}
 */


